import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { WhatsappLogo, Copy, Envelope, ShareNetwork, Buildings } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

export default function InviteDriverModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const qrRef = useRef(null);
  const [companyEmail, setCompanyEmail] = useState("");

  useEffect(() => {
    if (open) {
      setCompanyEmail(user?.email || "");
    }
  }, [open, user?.email]);

  const inviteUrl = useMemo(() => {
    if (!companyEmail) return "";
    const base = window.location.origin;
    return `${base}/register?empresa=${encodeURIComponent(companyEmail)}`;
  }, [companyEmail]);

  const shareText = `Você foi convidado(a) para se cadastrar como motorista no Acerto Rápido JVO. Clique no link e finalize seu cadastro:\n\n${inviteUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Link copiado!");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const openWhatsApp = () => {
    const msg = encodeURIComponent(shareText);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const openEmail = () => {
    const subject = encodeURIComponent("Convite para cadastro — Acerto Rápido JVO");
    const body = encodeURIComponent(shareText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Convite Acerto Rápido JVO", text: shareText, url: inviteUrl });
      } catch {}
    } else {
      copy();
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = "convite-motorista.png";
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invite-driver-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <ShareNetwork size={26} weight="duotone" className="text-blue-600" /> Convidar Motorista
          </DialogTitle>
          <DialogDescription>
            Envie este link/QR para o motorista. O e-mail da empresa já virá <strong>pré-preenchido</strong> no cadastro dele.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider flex items-center gap-1"><Buildings size={12} /> E-mail da sua empresa</Label>
            <Input
              data-testid="invite-company-email"
              type="email"
              value={companyEmail}
              onChange={(e) => setCompanyEmail(e.target.value)}
              placeholder="empresa@exemplo.com"
              className="mt-1"
            />
            <p className="text-xs text-slate-500 mt-1">Este é o e-mail que você usou ao cadastrar a empresa.</p>
          </div>

          {inviteUrl && (
            <>
              <div ref={qrRef} className="flex justify-center bg-white p-3 rounded-lg border">
                <QRCodeCanvas
                  data-testid="invite-qr"
                  value={inviteUrl}
                  size={200}
                  level="M"
                  includeMargin={false}
                  fgColor="#1e3a8a"
                />
              </div>

              <div className="flex items-stretch gap-2">
                <Input data-testid="invite-link" value={inviteUrl} readOnly className="text-xs" />
                <Button data-testid="btn-copy-invite" variant="outline" onClick={copy} title="Copiar">
                  <Copy size={16} />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="btn-invite-whatsapp" onClick={openWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
                  <WhatsappLogo size={18} weight="fill" /> WhatsApp
                </Button>
                <Button data-testid="btn-invite-email" onClick={openEmail} variant="outline" className="gap-2">
                  <Envelope size={18} weight="duotone" /> E-mail
                </Button>
                <Button data-testid="btn-invite-download-qr" onClick={downloadQR} variant="outline" className="gap-2 col-span-2">
                  Baixar QR Code
                </Button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <Button data-testid="btn-invite-share" onClick={share} variant="outline" className="gap-2 col-span-2">
                    <ShareNetwork size={18} weight="duotone" /> Compartilhar
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
